'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { HeroBanner } from '@/components/frontend/common/hero-banner';
import { IntroSection } from './common/intro-section';
import { WhatIsSection } from './common/what-is-section';
import { WhatIsBitcoinMlmSection } from './common/what-is-bitcoin-mlm-section';
import { FeaturesTwoColumnSection } from './common/features-two-column-section';
import { BenefitsGridSection } from './common/benefits-grid-section';
import { OfferSection } from './common/offer-section';
import { LucideIcon } from 'lucide-react';

interface Feature {
    icon: LucideIcon;
    title: string;
    description: string;
}

interface FeatureDetailContentProps {
    pageKey: string;
    translationNamespace: string;
    features: Feature[];
    benefits: string[];
    ctaIcon: LucideIcon;
    showWhatIsBitcoin?: boolean;
    introImageSrc: string;
    introImageAlt?: string;
    introParagraphKeys?: string[];
    introSectionHeadingPage?: string;
    introSectionHeadingTitle?: string;
    introSectionHeadingPill?: string;
    featureTitle?: string;
    featureDescription?: string;
    featurePill?: string;
    useLocaleContent?: boolean; // If true, use locale translations instead of API
}

export function FeatureDetailContent({
    pageKey,
    translationNamespace,
    features,
    benefits,
    ctaIcon,
    showWhatIsBitcoin = false,
    introImageSrc,
    introImageAlt,
    introParagraphKeys,
    introSectionHeadingPage,
    introSectionHeadingTitle,
    introSectionHeadingPill,
    featureTitle,
    featureDescription,
    featurePill,
    useLocaleContent = false,
}: FeatureDetailContentProps) {
    const t = useTranslations(translationNamespace);
    const tFeatures = useTranslations('features');
    const whatIsT = useTranslations(`${translationNamespace}.whatIsBitcoin`);
    const locale = useLocale();

    // Debug: Log meta details from page head AND fetch from API to compare
    useEffect(() => {
        const title = document.querySelector('title')?.textContent || '';
        const metaDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
        const metaKeywords = document.querySelector('meta[name="keywords"]')?.getAttribute('content') || '';

        // Also fetch from API to see what the server returned
        const fetchMetaFromAPI = async () => {
            try {
                const res = await fetch(`/api/features?identifier=${encodeURIComponent(pageKey)}&locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });
                if (res.ok) {
                    const data = await res.json();
                    const pageTitle = document.querySelector('title')?.textContent || '';

                    if (data.metaTitle && data.metaTitle !== pageTitle && locale === 'es') {
                        // Meta title mismatch detected

                    }
                }
            } catch (error) {

            }
        };

        fetchMetaFromAPI();
    }, [pageKey, locale]);

    // State for API-fetched feature data
    const [apiTitle, setApiTitle] = useState<string | null>(null);
    const [apiDescription, setApiDescription] = useState<string | null>(null);
    const [isLoadingFeature, setIsLoadingFeature] = useState(true);

    // Fetch feature data from API only if useLocaleContent is false
    useEffect(() => {
        // Skip API fetch if useLocaleContent is true
        if (useLocaleContent) {
            setIsLoadingFeature(false);
            return;
        }

        const fetchFeatureData = async () => {
            if (!pageKey) {
                setIsLoadingFeature(false);
                return;
            }

            try {
                setIsLoadingFeature(true);
                const apiUrl = `/api/features?identifier=${encodeURIComponent(pageKey)}&locale=${encodeURIComponent(locale)}`;

                const res = await fetch(apiUrl, {
                    cache: 'no-store',
                });

                if (!res.ok) {

                    setIsLoadingFeature(false);
                    return;
                }

                const data = await res.json();

                // Always use API data if available, regardless of locale
                // This ensures we use backend data instead of locale translations
                if (data && data.title) {
                    // Use API data even if it's English (fallback) - this is correct behavior
                    setApiTitle(data.title);
                    if (data.description) {
                        setApiDescription(data.description);
                    } else {
                        setApiDescription(null);
                    }
                } else {
                    // No data from API - clear states so it falls back to props/translations

                    setApiTitle(null);
                    setApiDescription(null);
                }
            } catch (error) {

                setApiTitle(null);
                setApiDescription(null);
            } finally {
                setIsLoadingFeature(false);
            }
        };

        fetchFeatureData();
    }, [pageKey, locale, useLocaleContent]);

    // Priority logic depends on useLocaleContent flag
    let heroTitle: string;
    let heroDescription: string;

    if (useLocaleContent) {
        // Use locale translations when useLocaleContent is true
        heroTitle = featureTitle || t('hero.title');
        heroDescription = featureDescription || t('hero.description');
    } else {
        // Priority: API data > Props > Translations
        // Always prioritize API data if it exists, regardless of locale
        const hasApiTitle = apiTitle !== null && apiTitle.trim() !== '';
        const hasApiDescription = apiDescription !== null && apiDescription.trim() !== '';
        const hasPropTitle = featureTitle !== undefined && featureTitle !== null && String(featureTitle).trim() !== '';
        const hasPropDescription = featureDescription !== undefined && featureDescription !== null && String(featureDescription).trim() !== '';

        heroTitle = hasApiTitle
            ? apiTitle
            : hasPropTitle
                ? String(featureTitle).trim()
                : t('hero.title');

        heroDescription = hasApiDescription
            ? apiDescription
            : hasPropDescription
                ? String(featureDescription).trim()
                : t('hero.description');
    }

    // Debug logging
    if (process.env.NODE_ENV === 'development') {
        // Development mode - debug info available
    }

    // Get pill label from translations - use the correct path
    const pillLabel = tFeatures('mlmFeatures.pillLabel');
    const heroPill = featurePill || pillLabel || 'mlm features';

    return (
        <div className="min-h-screen bg-white dark:bg-black">
            {/* Hero Section */}
            {/* Always fetch hero banner from API, but use translations as fallback */}
            <HeroBanner
                featureIdentifier={pageKey}
                title={heroTitle}
                description={heroDescription}
                pill={heroPill}
                className="border-b border-gray-200 dark:border-gray-900"
            />

            {/* Introduction Section */}
            <IntroSection
                translationNamespace={`${translationNamespace}.intro`}
                paragraphKeys={introParagraphKeys}
                imageSrc={introImageSrc}
                imageAlt={introImageAlt}
                sectionHeadingPage={introSectionHeadingPage}
                sectionHeadingTitle={introSectionHeadingTitle}
                sectionHeadingPill={introSectionHeadingPill}
                pageKey={useLocaleContent ? undefined : pageKey}
                backendDescription={useLocaleContent ? undefined : (apiDescription || undefined)}
            />

            <WhatIsSection
                translationNamespace={`${translationNamespace}.whatIsBitcoin`}
                variant="gradient"
                showTwoColumnBlocks={true}
                subsectionKey="howBitcoinsMade"
                subsectionNamespace={`${translationNamespace}.whatIsBitcoin`}
                pill={whatIsT('pill')}
                sectionTitle={whatIsT('sectionTitle')}
            />

            {/* What is Bitcoin MLM Software Section */}
            <WhatIsBitcoinMlmSection
                translationNamespace={`${translationNamespace}.whatIsBitcoinMlm`}
                paragraphKeys={['description', 'description2']}
                imageSrc="/images/countries/best-mlm-software.webp"
                imageAlt={t('whatIsBitcoinMlm.imageAlt') || 'Bitcoin MLM Software'}
                sectionHeadingTitle={t('whatIsBitcoinMlm.title')}
            />

            {/* Features Section - Two Column Layout */}
            <FeaturesTwoColumnSection
                translationNamespace={`${translationNamespace}.features`}
                features={features}
            />

            {/* Benefits Section - Centered Header with List */}
            <BenefitsGridSection
                translationNamespace={`${translationNamespace}.benefits`}
                benefits={benefits}
            />

            {/* Offer Section */}
            <OfferSection translationNamespace={`${translationNamespace}.offer`} />

        </div>
    );
}

